import type { FieldConfig, FieldValues, IrsLimitsSet, NumberOrLimit, Option, StepItem, ValidationContext } from '../types';
import { useCalculator, useField } from '../state/CalculatorProvider';
import { CheckboxGroup } from './CheckboxGroup';
import { FieldRow } from './FieldRow';
import { NumericInput } from './NumericInput';
import { RadioGroup } from './RadioGroup';
import { Select } from './Select';

function resolveLimit(n: NumberOrLimit | undefined, limits: IrsLimitsSet, values: FieldValues): number | undefined {
  if (n === undefined) return undefined;
  return typeof n === 'function' ? n({ limits, values }) : n;
}

function resolveOptions(
  options: Option[] | ((v: FieldValues, ctx: ValidationContext) => Option[]),
  limits: IrsLimitsSet,
  values: FieldValues,
): Option[] {
  return typeof options === 'function' ? options(values, { limits, values }) : options;
}

/**
 * Field types not yet needed by a real calculator (slider lives in
 * engine/fields/Slider.tsx but is used directly by results views, not
 * through this declarative dispatch) land here as each phase actually
 * requires them — see docs/calculator-engine-architecture.md §5.
 */
function FieldItem<TValues extends FieldValues>({ field }: { field: FieldConfig<TValues> }) {
  const { config, limits, state } = useCalculator<TValues>();
  const { value, setValue, blur, error } = useField<TValues, keyof TValues>(field.name);
  // Namespaced by config.id, not just the bare field name. Found live: the
  // dev harness mounts every calculator on one page, and three separate
  // calculators had each independently picked the field name "planType" —
  // since a native <input name="..."> radio group is scoped to the whole
  // document, not to a React subtree, all three groups fought over which
  // option was "checked" the instant more than one was on the page at once.
  // A pure implementation fix (no calculator would ever want this
  // cross-talk), not a business-rule call — same bucket as not reproducing
  // sendToPrint(). Doesn't cover the same calculator mounted twice on one
  // page (the dev harness's own theming-preview section does this for
  // display only, never interacted with) — narrow enough to accept.
  const id = `hsab-calc-field-${config.id}-${field.name}`;
  const label = typeof field.label === 'function' ? field.label(state.values, { limits }) : field.label;
  const visible = !field.visibleIf || field.visibleIf(state.values, { limits, values: state.values });

  if (!visible) return null;

  if (field.type === 'radioGroup') {
    return (
      <FieldRow id={id} label={label} srLabel={field.srLabel} tooltip={field.tooltip} help={field.help} error={error}>
        <RadioGroup
          id={id}
          name={id}
          value={value as string | number | boolean | null}
          options={field.options}
          layout={field.layout}
          onChange={(v) => setValue(v as TValues[keyof TValues])}
          onBlur={blur}
          error={error}
        />
      </FieldRow>
    );
  }

  if (field.type === 'checkboxGroup') {
    return (
      <FieldRow id={id} label={label} srLabel={field.srLabel} tooltip={field.tooltip} help={field.help} error={error}>
        <CheckboxGroup
          id={id}
          value={value as (string | number)[] | null}
          options={field.options}
          exclusiveValue={field.exclusiveValue}
          onChange={(v) => setValue(v as TValues[keyof TValues])}
          onBlur={blur}
          error={error}
        />
      </FieldRow>
    );
  }

  if (field.type === 'number' || field.type === 'currency' || field.type === 'percent') {
    const prefix = field.type === 'currency' ? '$' : undefined;
    const suffix = field.type === 'percent' ? '%' : undefined;
    return (
      <FieldRow id={id} label={label} srLabel={field.srLabel} tooltip={field.tooltip} help={field.help} error={error}>
        <NumericInput
          id={id}
          value={value as number | null}
          onChange={(v) => setValue(v as TValues[keyof TValues])}
          onBlur={blur}
          error={error}
          min={resolveLimit(field.min, limits, state.values)}
          max={resolveLimit(field.max, limits, state.values)}
          step={field.step}
          stepper={field.stepper}
          snapTo={field.type === 'percent' || field.type === 'number' ? field.snapTo : undefined}
          prefix={prefix}
          suffix={suffix}
          stepperLayout={config.stepperLayout}
          stepperLabels={config.stepperLabels}
          inputStyle={config.inputStyle}
          invalidInput={config.invalidInput}
        />
      </FieldRow>
    );
  }

  if (field.type === 'text') {
    return (
      <FieldRow id={id} label={label} srLabel={field.srLabel} tooltip={field.tooltip} help={field.help} error={error}>
        <input
          id={id}
          type="text"
          className="hsab-calc__text-input"
          value={(value as string) ?? ''}
          onChange={(e) => setValue(e.target.value as TValues[keyof TValues])}
          onBlur={blur}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        />
      </FieldRow>
    );
  }

  if (field.type === 'select') {
    return (
      <FieldRow id={id} label={label} srLabel={field.srLabel} tooltip={field.tooltip} help={field.help} error={error}>
        <Select
          id={id}
          value={value as string | number | null}
          options={resolveOptions(field.options, limits, state.values)}
          onChange={(v) => setValue(v as TValues[keyof TValues])}
          onBlur={blur}
          error={error}
        />
      </FieldRow>
    );
  }

  if (field.type === 'custom') {
    return (
      <>
        {field.render({
          value,
          setValue: (v) => setValue(v as TValues[keyof TValues]),
          blur,
          error,
          touched: false,
          id,
        })}
      </>
    );
  }

  return null;
}

export function renderStepItem<TValues extends FieldValues>(
  item: StepItem<TValues>,
  index: number,
  values: TValues,
  setValue: <K extends keyof TValues>(name: K, value: TValues[K]) => void,
) {
  if (item.kind === 'field') {
    // Keyed by position, not item.name — two FieldConfigs can legitimately
    // share a name (e.g. Plan Comparison's HSA vs. PPO variant of "average
    // doctor visit cost", mutually exclusive via visibleIf, writing to the
    // same values key). Only one ever actually renders at a time; keying
    // by name alone would collide.
    return <FieldItem key={`field-${index}`} field={item} />;
  }
  if (item.kind === 'heading') {
    if (item.visibleIf && !item.visibleIf(values)) return null;
    const text = typeof item.text === 'function' ? item.text(values) : item.text;
    return (
      <h3 key={`heading-${index}`} className="hsab-calc__step-heading">
        {text}
      </h3>
    );
  }
  if (item.kind === 'note') {
    if (item.visibleIf && !item.visibleIf(values)) return null;
    const content = typeof item.content === 'function' ? item.content(values) : item.content;
    return (
      <div key={`note-${index}`} className="hsab-calc__step-note">
        {content}
      </div>
    );
  }
  if (item.kind === 'custom') {
    return <div key={item.id}>{item.render({ values, setValue })}</div>;
  }
  return null;
}
